// Copyright (c) 2019 Deere & Company

import Claim, {getDefaultState as getClaimDefaultState} from '@deere/cps-it.warranty-model-factory/lib/claim-incentive';
import {
    addEmptyClaimRows,
    removeEmptyRows
} from '../../shared/services/empty-claim-rows';
import {
    findMatchingAccount,
    findMatchingKey,
    findMatchingSelectedProgram,
    getPartUnit
} from '../../shared/services/claim-shared-helper';
import ClaimAction from '@deere/cps-it.warranty-model-factory/lib/claim-action';
import ClaimComplaintCause from '@deere/cps-it.warranty-model-factory/lib/claim-complaint-cause';
import actions from '../../action-list';
import {getDefaultState as getClaimPartDefaultState} from '@deere/cps-it.warranty-model-factory/lib/claim-item-part';
import {getDefaultState} from '../state';
import {getDefaultState as getLaborDefaultState} from '@deere/cps-it.warranty-model-factory/lib/claim-item-labor';
import {getDefaultState as getOtherCreditDefaultState} from '@deere/cps-it.warranty-model-factory/lib/claim-item-other-credit';

const updateResponsibleAccounts = (state, action) => Claim.update(state, {
    responsibleAccount: {
        $set: findMatchingAccount(action.accountId, action.dealerCollection)
    }
});

const resetIncentiveClaim = () =>
    getClaimDefaultState();

const setIncentiveClaim = (state, action) =>
    Claim.update(state, {
        $set: action.updatedClaim
    });

const reviseIncentiveClaim = state =>
    Claim.update(state, {
        errors: {
            $set: []
        }
    });

const updateWorkOrderOne = (state, action) =>
Claim.update(state, {
    referenceInfo: {
        referenceNumber1: {
            $set: action.workOrder
        }
    }
});

const updateWorkOrderTwo = (state, action) =>
Claim.update(state, {
    referenceInfo: {
        referenceNumber2: {
            $set: action.workOrder
        }
    }
});

const updateCustomerName = (state, action) =>
Claim.update(state, {
    customer: {
        customerName: {
            $set: action.customerName
        }
    }
});

const updateEmail = (state, action) =>
Claim.update(state, {
    customer: {
        email: {
            $set: action.email
        }
    }
});

const updateServiceDate = (state, action) =>
Claim.update(state, {
    failureInfo: {
        repairDate: {
            $set: action.date
        }
    }
});

const updateFailurePart = (state, action) =>
Claim.update(state, {
    failureInfo: {
        failurePartNumber: {
            $set: action.partNumber
        }
    }
});

const updateUseAmount = (state, action) =>
Claim.update(state, {
    useAmounts: {
        [action.index]: {
            amount: {
                $set: action.amount
            }
        }
    }
});

const updateUseAmountOnProductLoad = (state, action) => Claim.update(state, {
    useAmounts: {
        $push: [action.useAmount]
    }
});

const updateUseAmounts = (state, action) =>
Claim.update(state, {
    useAmounts: {
        $set: action.useAmounts
    }
});

const updateRepairNumber = (state, action) =>
Claim.update(state, {
    referenceInfo: {
        repairNumber: {
            $set: action.repairNumber
        }
    }
});

const updateUnitDropDown = (state, action) =>
Claim.update(state, {
    useAmounts: {
        0: {
            unit: {
                $set: findMatchingKey(action.key, action.unitCollection)
            }
        }
    }
});

const updatePin = (state, action) => Claim.update(state, {
    pin: {
        $set: action.pin
    }
});

const updateSelectedProgramDropDown = (state, action) =>
Claim.update(state, {
    [action.property]: {
        $set: findMatchingSelectedProgram(action.programName, action.collection)
    }
});

const updateClaimMode = (state, action) => Claim.update(state, {
    $set: action.isEditable ?
        addEmptyClaimRows(state, Claim) :
        removeEmptyRows(state, Claim)
});

const addFile = (state, action) => Claim.update(state, {
    file: {
        $push: [action.data]
    }
});

const deleteFile = (state, action) => {

    const index = state.file.findIndex(file => file.serialNumber === action.data.serialNumber && file.name === action.data.name);

    return Claim.update(state, {
        file: {
            $splice: [[index, 1]]
        }
    });

};

const updateFile = (state, action) => Claim.update(state, {
    file: {
        $set: action.files
    }
});

const updatePartDetails = (state, action) => Claim.update(state, {
    parts: {
        [action.index]: {
            $merge: {
                partDescription: action.partDetails.partDescription,
                partNumber: action.partDetails.partNumber,
                unit: getPartUnit(state.parts[action.index].unit, action.partDetails.unit[0])
            }
        }
    }
});

const createOtherCredit = state => Claim.update(state, {
    otherCredits: {
        $push: [getOtherCreditDefaultState()]
    }
});

const deleteOtherCredit = (state, action) => Claim.update(state, {
    otherCredits: {
        $splice: [[action.index, 1]]
    }
});

const updateCredits = (state, action) => Claim.update(state, {
    otherCredits: {
        [action.index]: {
            [action.property]: {
                $set: action.data
            }
        }
    }
});

const createLabor = state => Claim.update(state, {
    labor: {
        $push: [getLaborDefaultState()]
    }
});

const updateLaborType = (state, action) =>
    Claim.update(state, {
        labor: {
            [action.index]: {
                laborType: {
                    key: {
                        $set: action.laborType
                    }
                }
            }
        }
    });

const updateLaborHours = (state, action) => Claim.update(state, {
    labor: {
        [action.index]: {
            laborHours: {
                $set: action.laborHours
            }
        }
    }
});

const updateLaborLocation = (state, action) => Claim.update(state, {
    labor: {
        [action.index]: {
            location: {
                key: {
                    $set: action.location
                }
            }
        }
    }
});

const updateLaborComments = (state, action) => Claim.update(state, {
    labor: {
        [action.index]: {
            spgJobCode: {
                $set: action.spgJobCode
            }
        }
    }
});

const deleteLabor = (state, action) => Claim.update(state, {
    labor: {
        $splice: [[action.index, 1]]
    }
});

const updateTechId = (state, action) => Claim.update(state, {
    labor: {
        [action.index]: {
            techId: {
                $set: action.techId
            }
        }
    }
});

const addPart = state => Claim.update(state, {
    parts: {
        $push: [getClaimPartDefaultState()]
    }
});

const deletePart = (state, action) => Claim.update(state, {
    parts: {
        $splice: [[action.index, 1]]
    }
});

const updatePartProperty = (state, action) =>
    Claim.update(state, {
        parts: {
            [action.index]: {
                [action.property]: {
                    $set: action.data
                }
            }
        }
    });

const updatePartsUnit = (state, action) => Claim.update(state, {
    parts: {
        [action.index]: {
            unit: {
                $set: action.data
            }
        }
    }
});

const copyClaim = state => {

    const defaultClaim = getClaimDefaultState();

    return Claim.update(state, {
        $merge: {
            allowedActions: [
                new ClaimAction({
                    actionName: 'DRAFT',
                    id: 'DRAFT',
                    mode: 'CHG'
                }),
                new ClaimAction({
                    actionName: 'SAVE_VALD',
                    id: 'SAVE_VALD',
                    mode: 'CHG'
                })
            ],
            claimNumber: defaultClaim.claimNumber,
            claimStatus: defaultClaim.claimStatus,
            claimValueDetails: defaultClaim.claimValueDetails,
            complaintCauseCommentsBasis: new ClaimComplaintCause({
                ...state.complaintCauseCommentsBasis,
                comments: defaultClaim.complaintCauseCommentsBasis.comments
            }),
            errors: defaultClaim.errors,
            recordInfo: defaultClaim.recordInfo,
            reimbursementDetails: defaultClaim.reimbursementDetails
        }
    });

};

const resetSelectedProgram = state => {

    const defaultClaim = getClaimDefaultState();

    return Claim.update(state, {
        $merge: {
            selectedProgram: defaultClaim.selectedProgram
        }
    });

};

export default (state = getDefaultState().claim, action) => {

    const actionTypes = {
        [actions.ADD_CLAIM_INCENTIVE_FILE]: addFile,
        [actions.DELETE_CLAIM_INCENTIVE_FILE]: deleteFile,
        [actions.UPDATE_CLAIM_INCENTIVE_FILE_LIST]: updateFile,
        [actions.UPDATE_CLAIM_INCENTIVE_RESPONSIBLE_ACCOUNTS]: updateResponsibleAccounts,
        [actions.RESET_CLAIM_INCENTIVE]: resetIncentiveClaim,
        [actions.RESET_CLAIM_INCENTIVE_SELECTED_PROGRAM]: resetSelectedProgram,
        [actions.SET_CLAIM_INCENTIVE]: setIncentiveClaim,
        [actions.REVISE_CLAIM_INCENTIVE]: reviseIncentiveClaim,
        [actions.UPDATE_CLAIM_INCENTIVE_WORK_ORDER_ONE]: updateWorkOrderOne,
        [actions.UPDATE_CLAIM_INCENTIVE_WORK_ORDER_TWO]: updateWorkOrderTwo,
        [actions.UPDATE_CLAIM_INCENTIVE_CUSTOMER_NAME]: updateCustomerName,
        [actions.UPDATE_CLAIM_INCENTIVE_EMAIL]: updateEmail,
        [actions.UPDATE_CLAIM_INCENTIVE_REPAIR_DATE]: updateServiceDate,
        [actions.UPDATE_CLAIM_INCENTIVE_FAILURE_PART]: updateFailurePart,
        [actions.UPDATE_CLAIM_INCENTIVE_USE_AMOUNTS]: updateUseAmounts,
        [actions.UPDATE_CLAIM_INCENTIVE_USE_AMOUNT]: updateUseAmount,
        [actions.UPDATE_CLAIM_INCENTIVE_IS_EDITABLE]: updateClaimMode,
        [actions.UPDATE_CLAIM_INCENTIVE_REPAIR_NUMBER]: updateRepairNumber,
        [actions.UPDATE_CLAIM_INCENTIVE_USE_AMOUNT_UNIT]: updateUnitDropDown,
        [actions.UPDATE_CLAIM_INCENTIVE_PIN]: updatePin,
        [actions.UPDATE_CLAIM_INCENTIVE_SELECTED_PROGRAM]: updateSelectedProgramDropDown,
        [actions.SET_CLAIM_INCENTVE_PART_DETAILS]: updatePartDetails,
        [actions.CREATE_CLAIM_INCENTIVE_OTHER_CREDIT]: createOtherCredit,
        [actions.DELETE_CLAIM_INCENTIVE_OTHER_CREDIT]: deleteOtherCredit,
        [actions.UPDATE_CLAIM_INCENTIVE_OTHER_CREDIT_DESCRIPTION]: updateCredits,
        [actions.UPDATE_CLAIM_INCENTIVE_OTHER_CREDIT_AMOUNT]: updateCredits,
        [actions.UPDATE_CLAIM_INCENTIVE_OTHER_CREDIT_DISTANCE]: updateCredits,
        [actions.UPDATE_CLAIM_INCENTIVE_OTHER_CREDIT_COMMENTS]: updateCredits,
        [actions.CREATE_CLAIM_INCENTIVE_LABOR]: createLabor,
        [actions.UPDATE_CLAIM_INCENTIVE_LABOR_HOURS]: updateLaborHours,
        [actions.UPDATE_CLAIM_INCENTIVE_LABOR_LOCATION]: updateLaborLocation,
        [actions.UPDATE_CLAIM_INCENTIVE_LABOR_TYPE]: updateLaborType,
        [actions.UPDATE_CLAIM_INCENTIVE_LABOR_COMMENTS]: updateLaborComments,
        [actions.DELETE_CLAIM_INCENTIVE_LABOR]: deleteLabor,
        [actions.UPDATE_CLAIM_INCENTIVE_TECH_ID]: updateTechId,
        [actions.CREATE_CLAIM_INCENTIVE_PART]: addPart,
        [actions.DELETE_CLAIM_INCENTIVE_PART]: deletePart,
        [actions.UPDATE_CLAIM_INCENTIVE_PART_FIELDS]: updatePartProperty,
        [actions.UPDATE_CLAIM_INCENTIVE_PART_UNIT]: updatePartsUnit,
        [actions.COPY_CLAIM_INCENTIVE]: copyClaim,
        [actions.UPDATE_CLAIM_INCENTIVE_USE_AMOUNT_ON_PRODUCT_LOAD]: updateUseAmountOnProductLoad

    };
    const reducer = actionTypes[action.type];

    return reducer ?
        reducer(state, action) :
        state;

};




// WEBPACK FOOTER //
// ./src/claim/incentive/reducers/incentive-claim.js